import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import FormField from "@cloudscape-design/components/form-field";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Toggle from "@cloudscape-design/components/toggle";
import DatePicker from "@cloudscape-design/components/date-picker";
import AreaChart from "@cloudscape-design/components/area-chart";
import Container from "@cloudscape-design/components/container";
import Grid from "@cloudscape-design/components/grid";
import Tabs from "@cloudscape-design/components/tabs";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { chatPinnedAddModalStore, chatPinnedEditModalStore, tournamentsBannersAddModalStore } from "../stores/chatPinnedEditModal";
import { bannersStore, BannersStore } from "../stores/banners";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import { chatPinnedStore } from "../stores/chatPinned";
import DateTimeSelector from "../utils/component/DateTimeSelector";

const emptyDateRange = {
	type: "absolute",
	startDate: false,
	endDate: false,
};

const formatCtr = (value) => {
	const numericValue = Number(value);

	return `${Number.isFinite(numericValue) ? numericValue.toFixed(2) : "0.00"}%`;
};

class ChatPinnedEditModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			activeTabId: "settings",
			date: emptyDateRange,
			analyticsLoading: false,
			analyticsLoaded: false,
			analyticsData: [],
			unique: null,
			uniqueIdentityType: { label: "Accounts", value: "account" },
			totalView: 0,
			totalClick: 0,
			totalCtr: 0,
			analyticsError: null,
		};
	}
	componentDidUpdate(prevProps) {
		if (!prevProps.chatPinnedEditModal.show && this.props.chatPinnedEditModal.show) {
			this.resetFormFields();
		}
	}

	addChatPinned() {
		if (this.props.chatPinnedEditModal.isClickable && (typeof this.props.chatPinnedEditModal.link !== "string" || this.props.chatPinnedEditModal.link.trim() === "")) {
			this.props.Interface.addNoty("Error", "Internal link is required for clickable pinned messages");
			return;
		}

		this.setState({
			loading: true,
		});
		this.props.Interface.request(
			"set",
			{
				class: "data",
				module: "chatPinned",
				method: "edit",
			},
			{ ...this.props.chatPinnedEditModal, messageType: this.props.chatPinnedEditModal.messageType.value, lastDisplayed: `${this.props.chatPinnedEditModal.lastDisplayedDate}T${this.props.chatPinnedEditModal.lastDisplayedTime}` },
		)
			.then((result) => {
				this.setState({
					loading: false,
				});
				this.props.Interface.addNoty("Success", "Chat pinned was successfully added");
				this.props.setChatPinnedEditModal({ ...this.props.chatPinnedEditModal, show: false });
				this.props.Interface.loadChatPinnedList();
			})
			.catch((error) => {
				if (typeof error.text === "undefined") error = { text: error.toString() };
				this.props.setChatPinned({ ...this.props.chatPinned, loaded: true, error });

				this.setState({
					loading: false,
				});
			});
	}

	resetFormFields = () => {
		this.props.setChatPinnedEditModal({
			...this.props.chatPinnedEditModal,
			message: "",
			messageType: "",
			isClickable: false,
			link: "",
			type: false,
			status: false,
			durationMinutes: 0,
			intervalMinutes: 0,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});
	};

	updateParam = (key, value) => {
		const updatedState = cloneDeep(this.props.chatPinnedEditModal);
		updatedState[key] = value;
		this.props.setChatPinnedEditModal(updatedState);
	};

	isDateRangeValid = (range) => {
		if (!range || range.type !== "absolute") {
			return { valid: false, errorMessage: "Only Absolute mode" };
		}

		if (!range.startDate || !range.endDate) {
			return {
				valid: false,
				errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
			};
		}

		if (new Date(range.startDate) - new Date(range.endDate) > 0) {
			return {
				valid: false,
				errorMessage: "The selected date range is invalid. The start date must be before the end date.",
			};
		}

		return { valid: true };
	};

	loadAnalytics = (date = this.state.date) => {
		this.setState({
			analyticsLoading: true,
			analyticsError: null,
			totalView: 0,
			unique: null,
			totalClick: 0,
			totalCtr: 0,
		});

		this.props.Interface.loadChatPinnedAnalyticsInfo(this.props.chatPinnedEditModal.id, {
			from: date.startDate || false,
			to: date.endDate || false,
		})
			.then((analytics) => {
				this.setState({
					analyticsLoading: false,
					analyticsLoaded: true,
					analyticsData: analytics.data,
					unique: analytics.unique,
					totalView: analytics.totalView,
					totalClick: analytics.totalClick,
					totalCtr: analytics.totalCtr,
				});
			})
			.catch((error) => {
				this.setState({
					analyticsLoading: false,
					analyticsLoaded: true,
					analyticsData: [],
					analyticsError: error,
				});
			});
	};

	handleTabChange = ({ detail }) => {
		const shouldLoadAnalytics = detail.activeTabId === "statistics" && !this.state.analyticsLoaded && !this.state.analyticsLoading;

		this.setState({ activeTabId: detail.activeTabId }, () => {
			if (shouldLoadAnalytics) this.loadAnalytics();
		});
	};

	handleDateChange = ({ detail }) => {
		const date = detail.value || emptyDateRange;
		const shouldLoadAnalytics = !detail.value || this.isDateRangeValid(date).valid;

		this.setState({ date }, () => {
			if (shouldLoadAnalytics) this.loadAnalytics(date);
		});
	};

	renderUniqueStatistics() {
		const { unique, uniqueIdentityType, analyticsLoading, analyticsError } = this.state;
		const metrics = unique === null ? null : unique[uniqueIdentityType.value];
		const data = metrics === null ? [] : metrics.data;
		const xDomain = data.length > 0 ? [new Date(data[0].date), new Date(data[data.length - 1].date)] : undefined;
		const metricDefinitions = [
			{ title: "Unique Views", field: "view", total: "totalView", color: "text-status-error" },
			{ title: "Unique Clicks", field: "click", total: "totalClick", color: "text-status-info" },
			{ title: "Unique CTR", field: "ctr", total: "totalCtr", color: "text-status-success" },
		];

		return (
			<Container>
				<SpaceBetween size="m">
					<FormField label="Unique metrics for">
						<Select selectedOption={uniqueIdentityType} onChange={({ detail }) => this.setState({ uniqueIdentityType: detail.selectedOption })} options={[{ label: "Accounts", value: "account" }, { label: "Guests", value: "guest" }, { label: "Combined", value: "combined" }]} />
					</FormField>
					<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
						{metricDefinitions.map((metric) => (
							<div key={metric.field} style={{ textAlign: "center", padding: "5px", background: "rgb(50 66 82 / 10%)", borderRadius: "8px" }}>
								<Box variant="h1" color={metric.color}>{metrics === null || metrics[metric.total] === null ? "—" : metric.field === "ctr" ? formatCtr(metrics[metric.total]) : metrics[metric.total]}</Box>
								<Box variant="small" color="text-label">{metric.title}</Box>
							</div>
						))}
					</Grid>
					<AreaChart
						statusType={analyticsError ? "error" : analyticsLoading ? "loading" : "finished"}
						errorText={analyticsError?.text || analyticsError?.message || "Couldn't load pinned message statistics"}
						series={data.length > 0 ? metricDefinitions.map((metric) => ({
							title: metric.title,
							type: "area",
							data: data.map((item) => ({ x: new Date(item.date), y: item[metric.field] })),
							...(metric.field === "ctr" ? { valueFormatter: formatCtr } : {}),
						})) : []}
						xDomain={xDomain}
						i18nStrings={{
							filterPlaceholder: "Choose metric",
							detailTotalFormatter: () => "",
							xTickFormatter: (value) => value.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
							yTickFormatter: (value) => value.toFixed(2),
						}}
						height={300}
						xScaleType="time"
						ariaLabel={`Daily unique pinned message metrics for ${uniqueIdentityType.label.toLowerCase()}`}
						yTitle=""
						empty={<Box textAlign="center">No unique data for this category in the selected period.</Box>}
						noMatch={<Box textAlign="center">No matching data</Box>}
					/>
				</SpaceBetween>
			</Container>
		);
	}

	renderStatistics() {
		const { analyticsData, analyticsError, analyticsLoading } = this.state;
		const xDomain = analyticsData.length > 0 ? [new Date(analyticsData[0].date), new Date(analyticsData[analyticsData.length - 1].date)] : undefined;

		return (
			<SpaceBetween size="l">
				<DateRangePicker
					onChange={this.handleDateChange}
					value={this.state.date}
					isValidRange={this.isDateRangeValid}
					i18nStrings={{
						todayAriaLabel: "Today",
						nextMonthAriaLabel: "Next month",
						previousMonthAriaLabel: "Previous month",
						customRelativeRangeDurationLabel: "Duration",
						customRelativeRangeDurationPlaceholder: "Enter duration",
						customRelativeRangeOptionLabel: "Custom range",
						customRelativeRangeOptionDescription: "Define a custom range in the past",
						customRelativeRangeUnitLabel: "Unit of time",
						formatRelativeRange: ({ startDate, endDate }) => {
							const start = new Date(startDate);
							const end = new Date(endDate);
							const locale = "en-GB";
							const dateOptions = { year: "numeric", month: "long", day: "numeric" };
							const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

							return `${start.toLocaleDateString(locale, dateOptions)} ${start.toLocaleTimeString(locale, timeOptions)} - ${end.toLocaleDateString(locale, dateOptions)} ${end.toLocaleTimeString(locale, timeOptions)}`;
						},
						formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
						dateTimeConstraintText: "Date and time must be within the selected range.",
						renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
						labelRelativeRange: "Relative range",
						labelAbsoluteRange: "Absolute range",
						dateTimePickerAriaLabel: "Date and time picker",
						dateTimePickerLabel: "Date and Time",
						clearButtonAriaLabel: "Clear date range",
						relativeModeTitle: "Relative mode",
						absoluteModeTitle: "Absolute mode",
						cancelButtonLabel: "Cancel",
						applyButtonLabel: "Apply",
						clearButtonLabel: "Clear and dismiss",
					}}
					dateOnly
					relativeOptions={[]}
					rangeSelectorMode="absolute-only"
					expandToViewport
					hideTimeOffset
					disabled={analyticsLoading}
					placeholder="from/to"
					showClearButton={true}
				/>
				<Container>
					<AreaChart
						statusType={analyticsError ? "error" : analyticsLoading ? "loading" : "finished"}
						errorText={analyticsError?.text || analyticsError?.message || "Couldn't load pinned message statistics"}
						series={
							analyticsData.length > 0
								? [
										{
											title: "Views",
											type: "area",
											data: analyticsData.map((item) => ({ x: new Date(item.date), y: item.view })),
										},
										{
											title: "Clicks",
											type: "area",
											data: analyticsData.map((item) => ({ x: new Date(item.date), y: item.click })),
										},
										{
											title: "CTR",
											type: "area",
											data: analyticsData.map((item) => ({ x: new Date(item.date), y: Number.isFinite(Number(item.ctr)) ? Number(item.ctr) : 0 })),
											valueFormatter: (value) => formatCtr(value),
										},
								  ]
								: []
						}
						xDomain={xDomain}
						i18nStrings={{
							filterPlaceholder: "Chose column ",
							detailTotalFormatter: () => "",
							xTickFormatter: (value) =>
								value
									.toLocaleDateString("en-US", {
										month: "short",
										day: "numeric",
									})
									.split(",")
									.join("\n"),
							yTickFormatter: (value) => (Math.abs(value) >= 1e9 ? (value / 1e9).toFixed(1).replace(/\.0$/, "") + "G" : Math.abs(value) >= 1e6 ? (value / 1e6).toFixed(1).replace(/\.0$/, "") + "M" : Math.abs(value) >= 1e3 ? (value / 1e3).toFixed(1).replace(/\.0$/, "") + "K" : value.toFixed(2)),
						}}
						height={300}
						xScaleType="time"
						ariaLabel="Stacked area chart, pinned message metrics"
						yTitle=""
						empty={
							<Box textAlign="center" color="inherit">
								<b>No data available</b>
								<Box variant="p" color="inherit">
									Try adjusting your filters
								</Box>
							</Box>
						}
						noMatch={
							<Box textAlign="center" color="inherit">
								<b>No matching data</b>
								<Box variant="p" color="inherit">
									Try adjusting your filters
								</Box>
							</Box>
						}
					/>
				</Container>
				<Container>
					<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
						<div style={{ textAlign: "center", padding: "5px", background: "rgb(50 66 82 / 10%)", borderRadius: "8px" }}>
							<Box variant="h1" color="text-status-error">
								{this.state.totalView}
							</Box>
							<Box variant="small" color="text-label">
								Total Views
							</Box>
						</div>
						<div style={{ textAlign: "center", padding: "5px", background: "rgb(50 66 82 / 10%)", borderRadius: "8px" }}>
							<Box variant="h1" color="text-status-info">
								{this.state.totalClick}
							</Box>
							<Box variant="small" color="text-label">
								Total Clicks
							</Box>
						</div>
						<div style={{ textAlign: "center", padding: "5px", background: "rgb(50 66 82 / 10%)", borderRadius: "8px" }}>
							<Box variant="h1" color="text-status-success">
								{formatCtr(this.state.totalCtr)}
							</Box>
							<Box variant="small" color="text-label">
								Total CTR
							</Box>
						</div>
					</Grid>
					<Box variant="p">Total metrics include all recorded views and clicks from accounts and guests.</Box>
				</Container>
				{this.renderUniqueStatistics()}
			</SpaceBetween>
		);
	}

	render() {
		const { chatPinnedEditModal, setChatPinnedEditModal } = this.props;
		return (
			<Modal
				visible={chatPinnedEditModal.show}
				onDismiss={() => setChatPinnedEditModal({ ...chatPinnedEditModal, show: false })}
				size="large"
				header="Edit pinned message"
				children={
					<Tabs
						activeTabId={this.state.activeTabId}
						onChange={this.handleTabChange}
						tabs={[
							{
								label: "Settings",
								id: "settings",
								content: (
									<SpaceBetween direction="vertical" size="m">
										<Box variant="span">Message</Box>
										<Input
											name="Title"
											autoComplete="off"
											inputMode="text"
											type="text"
											disableBrowserAutocorrect={true}
											placeholder="Message"
											value={chatPinnedEditModal.message}
											onChange={(event) =>
												setChatPinnedEditModal({
													...chatPinnedEditModal,
													message: event.detail.value,
												})
											}
										/>
										<Box variant="span">Message translation type</Box>
										<Select placeholder="Select type" selectedOption={chatPinnedEditModal.messageType} onChange={(option) => this.updateParam("messageType", option.detail.selectedOption)} options={chatPinnedEditModal.currentOption.messageType} />
										<Toggle checked={chatPinnedEditModal.isClickable} onChange={({ detail }) => this.updateParam("isClickable", detail.checked)}>
											{chatPinnedEditModal.isClickable ? "Clickable" : "Not clickable"}
										</Toggle>
										{chatPinnedEditModal.isClickable && (
											<FormField label="Internal link" errorText={chatPinnedEditModal.link.startsWith("/") ? "Enter the internal path without a leading /, e.g. promo" : undefined}>
												<Input name="Link" autoComplete="off" inputMode="url" type="text" disableBrowserAutocorrect={true} placeholder="Internal link" value={chatPinnedEditModal.link} onChange={(event) => this.updateParam("link", event.detail.value)} />
											</FormField>
										)}
										<Box variant="span">Type</Box>
										<Select filteringType="auto" placeholder="Select type" selectedOption={chatPinnedEditModal.type} onChange={(option) => this.updateParam("type", option.detail.selectedOption)} options={chatPinnedEditModal.currentOption.type} />

										{chatPinnedEditModal.type.value === "interval" && (
											<>
												<Box variant="span">Duration minutes</Box>
												<Input
													name="durationMinutes"
													autoComplete="off"
													inputMode="number"
													type="number"
													disableBrowserAutocorrect={true}
													placeholder="Duration minutes"
													value={chatPinnedEditModal.durationMinutes}
													onChange={(event) =>
														setChatPinnedEditModal({
															...chatPinnedEditModal,
															durationMinutes: event.detail.value,
														})
													}
												/>
												<Box variant="span">Interval minutes</Box>
												<Input
													name="intervalMinutes"
													autoComplete="off"
													inputMode="number"
													type="number"
													disableBrowserAutocorrect={true}
													placeholder="Interval minutes"
													value={chatPinnedEditModal.intervalMinutes}
													onChange={(event) =>
														setChatPinnedEditModal({
															...chatPinnedEditModal,
															intervalMinutes: event.detail.value,
														})
													}
												/>
											</>
										)}
										<Box variant="span">Last displayed</Box>
										<DateTimeSelector
											defaultDate={chatPinnedEditModal.lastDisplayedDate}
											defaultTime={chatPinnedEditModal.lastDisplayedTime}
											onTimeChange={(time) => {
												setChatPinnedEditModal({
													...chatPinnedEditModal,
													lastDisplayedTime: time,
												});
											}}
											onDateChange={(date) => {
												setChatPinnedEditModal({
													...chatPinnedEditModal,
													lastDisplayedDate: date,
												});
											}}
										/>
										<Box variant="span">Status</Box>
										<Select filteringType="auto" placeholder="Select status" selectedOption={chatPinnedEditModal.status} onChange={(option) => this.updateParam("status", option.detail.selectedOption)} options={chatPinnedEditModal.currentOption.status} />
									</SpaceBetween>
								),
							},
							{
								label: "Statistics",
								id: "statistics",
								content: this.renderStatistics(),
							},
						]}
					/>
				}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="m">
							<Button variant="normal" onClick={() => setChatPinnedEditModal({ ...chatPinnedEditModal, show: false })} children="Close" />
							<Button variant="primary" disabled={this.state.loading} onClick={() => this.addChatPinned()} children="Ok" />
						</SpaceBetween>
					</Box>
				}
			/>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const chatPinnedEditModal = useRecoilValue(chatPinnedEditModalStore);
		const setChatPinnedEditModal = useSetRecoilState(chatPinnedEditModalStore);
		const chatPinned = useRecoilValue(chatPinnedStore);
		const setChatPinned = useSetRecoilState(chatPinnedStore);
		return <Component {...props} chatPinned={chatPinned} setChatPinned={setChatPinned} setChatPinnedEditModal={setChatPinnedEditModal} chatPinnedEditModal={chatPinnedEditModal} />;
	};
}

export default withRecoil(ChatPinnedEditModal);
